import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import apiRequest from '../../lib/http'
import { businessTimeZone } from '../../utils/date'

export const loadCandidates = createAsyncThunk(
  'bulkProcessing/loadCandidates',
  async ({ from, to }, { rejectWithValue }) => {
    try {
      const response = await apiRequest.post('/payments/bulk-candidates', {
        from,
        to,
        timezone: businessTimeZone
      })
      return response.data
    } catch (error) {
      const errorMessage = error.response?.data?.error?.errors?.[0]?.message || 'Failed to load candidates'
      return rejectWithValue(errorMessage)
    }
  }
)

export const processBatch = createAsyncThunk(
  'bulkProcessing/processBatch',
  async ({ items }, { rejectWithValue }) => {
    try {
      const response = await apiRequest.post('/payments/bulk-process', { items })
      return response.data
    } catch (error) {
      const errorMessage = error.response?.data?.error?.errors?.[0]?.message || 'Failed to process batch'
      return rejectWithValue(errorMessage)
    }
  }
)

// Approve selected review-mode payments for processing. This does NOT charge —
// it marks them ready; the payment worker (every 15 min, business hrs) charges
// them via their chosen gateway shortly after.
export const approveBatch = createAsyncThunk(
  'bulkProcessing/approveBatch',
  async ({ items }, { rejectWithValue }) => {
    try {
      const response = await apiRequest.post('/payments/bulk-approve', { items })
      return response.data
    } catch (error) {
      const errorMessage = error.response?.data?.error?.errors?.[0]?.message || 'Failed to approve batch'
      return rejectWithValue(errorMessage)
    }
  }
)

export const refetchResults = createAsyncThunk(
  'bulkProcessing/refetchResults',
  async ({ paymentIds } = {}, { rejectWithValue }) => {
    try {
      const response = await apiRequest.post('/payments/bulk-refetch', { paymentIds })
      return response.data
    } catch (error) {
      const errorMessage = error.response?.data?.error?.errors?.[0]?.message || 'Failed to refetch results'
      return rejectWithValue(errorMessage)
    }
  }
)

// Transactions -> Processed Payments history screen (Screen 4). Backend endpoint
// does not exist yet at the time of writing — this is built against the defined
// contract in docs/plans (see ProcessedPayments container for details) and is
// mocked in tests. A matching backend endpoint will follow.
export const loadProcessedHistory = createAsyncThunk(
  'bulkProcessing/loadProcessedHistory',
  async ({ from, to, basis }, { rejectWithValue }) => {
    try {
      const response = await apiRequest.post('/payments/processed-history', {
        from,
        to,
        timezone: businessTimeZone,
        // Which instant the date window bounds: 'submitted' (Processed — what we sent that day)
        // or 'settled' (Settled — what resolved that day). Omitted => backend default 'submitted'.
        ...(basis ? { basis } : {}),
      })
      return response.data
    } catch (error) {
      const errorMessage =
        error.response?.data?.error?.errors?.[0]?.message || 'Failed to load processed payments history'
      return rejectWithValue(errorMessage)
    }
  }
)

export const excludePayments = createAsyncThunk(
  'bulkProcessing/excludePayments',
  async ({ paymentIds, reason }, { rejectWithValue }) => {
    try {
      const response = await apiRequest.post('/payments/bulk-exclude', { paymentIds, reason })
      return response.data
    } catch (error) {
      const errorMessage = error.response?.data?.error?.errors?.[0]?.message || 'Failed to skip payments'
      return rejectWithValue(errorMessage)
    }
  }
)

export const restorePayments = createAsyncThunk(
  'bulkProcessing/restorePayments',
  async ({ paymentIds }, { rejectWithValue }) => {
    try {
      const response = await apiRequest.post('/payments/bulk-restore', { paymentIds })
      return response.data
    } catch (error) {
      const errorMessage = error.response?.data?.error?.errors?.[0]?.message || 'Failed to restore payments'
      return rejectWithValue(errorMessage)
    }
  }
)

export const loadExcluded = createAsyncThunk(
  'bulkProcessing/loadExcluded',
  async ({ from, to }, { rejectWithValue }) => {
    try {
      const response = await apiRequest.post('/payments/bulk-candidates', {
        from,
        to,
        timezone: businessTimeZone,
        view: 'excluded',
      })
      return response.data
    } catch (error) {
      const errorMessage = error.response?.data?.error?.errors?.[0]?.message || 'Failed to load skipped payments'
      return rejectWithValue(errorMessage)
    }
  }
)

const initialState = {
  rows: [],
  kpis: {},
  loading: false,
  error: null,
  selection: {},
  overrides: {},
  quickFilter: 'all',
  lastResults: null,
  lastApproval: null,
  refetching: false,
  history: {
    rows: [],
    kpis: {},
    settlementForecast: [],
  },
  historyLoading: false,
  historyError: null,
  selectedPaymentId: null,
  excludedRows: [],
  excludedLoading: false,
  skipping: false,
}

const bulkProcessingSlice = createSlice({
  name: 'bulkProcessing',
  initialState,
  reducers: {
    toggleRow: (state, action) => {
      const paymentId = action.payload
      state.selection[paymentId] = !state.selection[paymentId]
    },
    setSelection: (state, action) => {
      state.selection = action.payload
    },
    removeRow: (state, action) => {
      const paymentId = action.payload
      state.rows = state.rows.filter((row) => row.paymentId !== paymentId)
      delete state.selection[paymentId]
    },
    setOverride: (state, action) => {
      const { paymentId, patch } = action.payload
      state.overrides[paymentId] = { ...state.overrides[paymentId], ...patch }
    },
    setQuickFilter: (state, action) => {
      state.quickFilter = action.payload
    },
    clearResults: (state) => {
      state.lastResults = null
    },
    // Consume the one-shot approval/error notices after they've been shown, so a
    // remount (tab switch) doesn't replay a stale snackbar from a prior action.
    clearLastApproval: (state) => {
      state.lastApproval = null
    },
    clearError: (state) => {
      state.error = null
    },
    setSelectedPaymentId: (state, action) => {
      state.selectedPaymentId = action.payload
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadCandidates.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(loadCandidates.fulfilled, (state, action) => {
        state.loading = false
        const { kpis, rows } = action.payload.data
        const keepSelection = action.meta.arg?.keepSelection === true
        const previousSelection = state.selection
        const previousOverrides = state.overrides
        state.rows = rows
        state.kpis = kpis
        state.selection = rows.reduce((acc, row) => {
          acc[row.paymentId] = keepSelection ? !!previousSelection[row.paymentId] : !!row.defaultSelected
          return acc
        }, {})
        // Reset per-row overrides on reload — stale gateway/skip overrides from a
        // previous view must not silently re-apply to freshly loaded rows (which
        // may be entirely different payments).
        state.overrides = keepSelection
          ? rows.reduce((acc, row) => {
            if (previousOverrides[row.paymentId]) acc[row.paymentId] = previousOverrides[row.paymentId]
            return acc
          }, {})
          : {}
      })
      .addCase(loadCandidates.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      .addCase(processBatch.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(processBatch.fulfilled, (state, action) => {
        state.loading = false
        state.lastResults = action.payload.data
      })
      .addCase(processBatch.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      .addCase(approveBatch.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(approveBatch.fulfilled, (state, action) => {
        state.loading = false
        state.lastApproval = action.payload.data
        // Clear per-row overrides after a successful approval so they don't leak
        // onto the reloaded queue.
        state.overrides = {}
      })
      .addCase(approveBatch.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      .addCase(refetchResults.pending, (state) => {
        state.refetching = true
        state.error = null
      })
      .addCase(refetchResults.fulfilled, (state, action) => {
        state.refetching = false
        const { results } = action.payload.data
        // Refetch is only ever triggered from the Processed Payments detail panel
        // (a history row), and returns a HISTORY-vocabulary status (cleared /
        // pending / returned / …). Write it ONLY into the history table — the
        // candidates table uses a different vocabulary (Ready / Needs Review), so
        // writing it there would show a foreign, wrong status on any candidate row
        // that happens to share the payment id.
        results.forEach((result) => {
          const historyRow = state.history.rows.find((r) => r.paymentId === result.paymentId)
          if (historyRow) {
            historyRow.status = result.status
            if (result.statusLabel) historyRow.statusLabel = result.statusLabel
            if (result.statusDetail) historyRow.statusDetail = result.statusDetail
          }
        })
      })
      .addCase(refetchResults.rejected, (state, action) => {
        state.refetching = false
        state.error = action.payload
      })

      .addCase(loadProcessedHistory.pending, (state) => {
        state.historyLoading = true
        state.historyError = null
      })
      .addCase(loadProcessedHistory.fulfilled, (state, action) => {
        state.historyLoading = false
        const { kpis, rows, settlementForecast } = action.payload.data
        state.history = { rows, kpis, settlementForecast: settlementForecast || [] }
      })
      .addCase(loadProcessedHistory.rejected, (state, action) => {
        state.historyLoading = false
        state.historyError = action.payload
      })

      .addCase(loadExcluded.pending, (state) => {
        state.excludedLoading = true
      })
      .addCase(loadExcluded.fulfilled, (state, action) => {
        state.excludedLoading = false
        const rows = action.payload?.data?.rows || []
        state.excludedRows = rows.filter((row) => row?.exclusion)
      })
      .addCase(loadExcluded.rejected, (state, action) => {
        state.excludedLoading = false
        state.error = action.payload
      })

      .addCase(excludePayments.pending, (state) => {
        state.skipping = true
      })
      .addCase(excludePayments.fulfilled, (state) => {
        state.skipping = false
      })
      .addCase(excludePayments.rejected, (state, action) => {
        state.skipping = false
        state.error = action.payload
      })

      .addCase(restorePayments.pending, (state) => {
        state.skipping = true
      })
      .addCase(restorePayments.fulfilled, (state) => {
        state.skipping = false
      })
      .addCase(restorePayments.rejected, (state, action) => {
        state.skipping = false
        state.error = action.payload
      })
  },
})

export const {
  toggleRow,
  setSelection,
  removeRow,
  setOverride,
  setQuickFilter,
  clearResults,
  clearLastApproval,
  clearError,
  setSelectedPaymentId,
} = bulkProcessingSlice.actions

export default bulkProcessingSlice.reducer
